import { config, useUserConfig } from '@littlebird/sdk';
import { useState } from 'react';

import { SaveableField } from '@/components/ui/SaveableField';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import { trackEvent } from '@/utils/analytics';

// Every meeting assist turn and screen includes these instructions, and the
// backend truncates them to this same length
const ASSIST_PROMPT_MAX_LENGTH = 2000;

type MeetingPromptKey =
  | 'meeting_prep_prompt'
  | 'meeting_summary_prompt'
  | 'meeting_assist_prompt';

function usePromptDraft(key: MeetingPromptKey) {
  // React Compiler would cache the saved prompt, a MobX read, so Discard
  // would restore the prompt from the first render
  'use no memo';
  const userConfig = useUserConfig();
  const savedPrompt = config.meetings[key];
  // null while the field shows the saved prompt, so a prompt saved on another
  // device shows up unless the user is editing
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? savedPrompt;
  const dirty = value !== savedPrompt;

  return {
    value,
    dirty,
    setValue: setDraft,
    save: () => {
      if (!dirty) return;
      userConfig.meetings.set(key, value);
      setDraft(null);
      trackEvent('MEETING_SETTING_PROMPT_UPDATED', { field: key });
    },
    discard: () => setDraft(null),
  };
}

export function useMeetingPromptFields() {
  return {
    prep: usePromptDraft('meeting_prep_prompt'),
    summary: usePromptDraft('meeting_summary_prompt'),
    assist: usePromptDraft('meeting_assist_prompt'),
  };
}

export type MeetingPromptFields = ReturnType<typeof useMeetingPromptFields>;

type MeetingPromptTextAreaProps = {
  className?: string;
  wrapperClassName?: string;
  fields: MeetingPromptFields;
};

export const MeetingPrepInstructionsTextArea = ({
  className,
  wrapperClassName,
  fields,
}: MeetingPromptTextAreaProps) => {
  return (
    <SaveableField
      id="meeting-prep-prompt"
      className={className}
      wrapperClassName={wrapperClassName}
      placeholder={
        // Skip rules only take effect behind this flag
        hasFeature(FEATURES.MEETING_INSTRUCTION_SKIPPED_STATUS)
          ? 'e.g. Skip prep for sales calls. Keep 1:1 prep short.'
          : 'Enter custom instructions for meeting prep'
      }
      value={fields.prep.value}
      dirty={fields.prep.dirty}
      onChange={fields.prep.setValue}
      onSave={fields.prep.save}
      onDiscard={fields.prep.discard}
      enableVoiceInput
    />
  );
};

export const MeetingSummaryInstructionsTextArea = ({
  className,
  wrapperClassName,
  fields,
}: MeetingPromptTextAreaProps) => {
  return (
    <SaveableField
      id="meeting-summary-prompt"
      className={className}
      wrapperClassName={wrapperClassName}
      placeholder="Enter custom instructions for meeting summaries"
      value={fields.summary.value}
      dirty={fields.summary.dirty}
      onChange={fields.summary.setValue}
      onSave={fields.summary.save}
      onDiscard={fields.summary.discard}
      enableVoiceInput
    />
  );
};

export const MeetingAssistInstructionsTextArea = ({
  className,
  wrapperClassName,
  fields,
}: MeetingPromptTextAreaProps) => {
  return (
    <SaveableField
      id="meeting-assist-prompt"
      className={className}
      wrapperClassName={wrapperClassName}
      placeholder="For example: file a Linear issue whenever a customer reports a bug. No Slack posts."
      value={fields.assist.value}
      dirty={fields.assist.dirty}
      onChange={(value) => {
        if (value.length <= ASSIST_PROMPT_MAX_LENGTH) {
          fields.assist.setValue(value);
        }
      }}
      onSave={fields.assist.save}
      onDiscard={fields.assist.discard}
      statusContent={
        <span className="text-xs text-foreground-muted">
          {fields.assist.value.length}/{ASSIST_PROMPT_MAX_LENGTH} characters
          {fields.assist.dirty && ' · Unsaved changes'}
        </span>
      }
      enableVoiceInput
    />
  );
};
